import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";
import { ApiError, portalApi, type PortalApiClient } from "../api/client";
import type { Session } from "../types";

type SessionState = "loading" | "authenticated" | "anonymous" | "expired" | "offline";

interface AuthContextValue {
  session: Session | null;
  state: SessionState;
  error: string | null;
  login(email: string, password: string, totpCode?: string): Promise<void>;
  logout(): Promise<void>;
  changePassword(currentPassword: string, newPassword: string): Promise<void>;
  refresh(): Promise<void>;
  setSession(session: Session): void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({
  children,
  client = portalApi,
}: PropsWithChildren<{ client?: PortalApiClient }>) {
  const [session, setSession] = useState<Session | null>(null);
  const [state, setState] = useState<SessionState>("loading");
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const current = await client.session.current();
      setSession(current);
      setState("authenticated");
      setError(null);
    } catch (requestError) {
      setSession(null);
      if (requestError instanceof ApiError && requestError.status === 401) {
        setState((previous) => (previous === "authenticated" ? "expired" : "anonymous"));
        setError(null);
      } else {
        setState("offline");
        setError("The portal cannot reach the server. Check the connection and try again.");
      }
    }
  }, [client]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      state,
      error,
      async login(email, password, totpCode) {
        const next = await client.session.login(email, password, totpCode);
        setSession(next);
        setState("authenticated");
        setError(null);
      },
      async logout() {
        try {
          await client.session.logout();
        } finally {
          setSession(null);
          setState("anonymous");
        }
      },
      async changePassword(currentPassword, newPassword) {
        const next = await client.session.changePassword(currentPassword, newPassword);
        setSession(next);
      },
      refresh,
      setSession(next) {
        setSession(next);
        setState("authenticated");
      },
    }),
    [client, error, refresh, session, state],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return value;
}
